import { armConfig, isArm, isExperiment } from '../../shared/experiment-arms.js';
import { normalizeSearch } from '../inference/runtime.js';

export const PARTICIPANT_STORAGE_KEY = 'privmeter-participant';

/**
 * The shape persisted to localStorage under PARTICIPANT_STORAGE_KEY, and held
 * in ParticipantGate's `session` state. Assembled from the enroll response in
 * ParticipantGate's enroll effect; finishedAt/finishReason are added later by
 * handleFinish once the participant confirms Finish.
 * @typedef {object} ParticipantSession
 * @property {string} prolificId
 * @property {string|null} studyId
 * @property {string|null} sessionId
 * @property {string} participantId
 * @property {string|null} sessionToken
 * @property {string} enrolledAt - ISO timestamp
 * @property {number} timeLimitMs
 * @property {number} revealDelayMs
 * @property {number} [reportGateDelayMs] - report-experiment report-gate
 *   delay from backend.study.reportGateDelayMinutes; 0 disables the gate.
 * @property {number} reportIntervalMs - legacy alias for reportGateDelayMs
 * @property {string} mode - the enroll response's `status` (e.g. 'mock', 'enrolled')
 * @property {string} experiment
 * @property {string} arm
 * @property {string|null} qualtricsBaseUrl - the App -> Qualtrics handoff base
 *   URL before Prolific/study params are appended (see harness/qualtrics.js
 *   buildQualtricsUrl for the final link), or null when the experiment has no
 *   mapped survey.
 * @property {string} [finishedAt] - ISO timestamp; set once the participant
 *   confirms Finish. Presence of this field is what routes ParticipantGate to
 *   CompleteScreen instead of the chat.
 * @property {string} [finishReason] - 'manual' | 'time' | 'tokens'
 */

const PROLIFIC_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

function parseSearch(search = '') {
  try {
    return new URLSearchParams(normalizeSearch(search));
  } catch (e) {
    console.warn('[participant-session] URLSearchParams failed', e);
    return new URLSearchParams();
  }
}

function trimmedOrNull(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function isValidProlificId(id) {
  const trimmed = trimmedOrNull(id);
  return Boolean(trimmed && PROLIFIC_ID_RE.test(trimmed));
}

export function readProlificParams(search = '') {
  const params = parseSearch(search);
  return {
    prolificId: trimmedOrNull(params.get('PROLIFIC_PID')),
    studyId: trimmedOrNull(params.get('STUDY_ID')),
    sessionId: trimmedOrNull(params.get('SESSION_ID')),
    experiment: trimmedOrNull(params.get('exp')),
    arm: trimmedOrNull(params.get('arm')),
  };
}

export function isResearcherRequested(search = '') {
  return parseSearch(search).get('researcher') === '1';
}

/**
 * @param {Storage} storage
 * @returns {ParticipantSession|null}
 */
export function loadSession(storage, key = PARTICIPANT_STORAGE_KEY) {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session && typeof session === 'object' ? session : null;
  } catch (e) {
    console.warn(`[localStorage read] ${key}`, e);
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {ParticipantSession} session
 */
export function saveSession(storage, session, key = PARTICIPANT_STORAGE_KEY) {
  try {
    storage?.setItem(key, JSON.stringify(session));
  } catch (e) {
    console.warn(`[localStorage write] ${key}`, e);
  }
}

export function clearSession(storage, key = PARTICIPANT_STORAGE_KEY) {
  try {
    storage?.removeItem(key);
  } catch (e) {
    console.warn(`[localStorage clear] ${key}`, e);
  }
}

const ARM_OVERRIDE_PROLIFIC_PREFIX = 'arm-';

/**
 * Dev/live-qa-only URL shorthand: `?arm=<arm>` alone (no PROLIFIC_PID)
 * selects an arm directly, mirroring the pre-Prolific-integration workflow.
 * Returns the arm name or null when absent/unrecognized.
 */
export function readArmOverrideParam(search = '') {
  const arm = trimmedOrNull(parseSearch(search).get('arm'));
  return isArm(arm) ? arm : null;
}

function buildArmOverrideSearch(search, arm) {
  const params = parseSearch(search);
  params.set('PROLIFIC_PID', `${ARM_OVERRIDE_PROLIFIC_PREFIX}${arm}`);
  params.set('STUDY_ID', armConfig(arm).experiment);
  params.delete('SESSION_ID');
  params.set('arm', arm);
  return `?${params.toString()}`;
}

/**
 * Resolves which localStorage key and effective search string identify the
 * active participant lock for this render. A `?arm=` override (see
 * readArmOverrideParam) is honored ONLY when the caller reports no existing
 * lock under the base storage key AND the URL carries no real PROLIFIC_PID —
 * an existing/real lock always wins, so this never interferes with a normal
 * Prolific or mock-mode session. When honored, it fabricates
 * `PROLIFIC_PID=arm-<arm>` and `STUDY_ID=<experiment>` so the rest of
 * resolveEntryState's Prolific-driven enroll/resume/conflict logic needs no
 * special-casing, and scopes storage to a per-arm key
 * (`${PARTICIPANT_STORAGE_KEY}:<arm>`) so finishing one arm never blocks a
 * different arm, and switching arms never conflicts with a prior arm's lock.
 * @param {string} search
 * @param {{hasBaseLock?: boolean}} [options]
 * @returns {{search: string, storageKey: string}}
 */
export function resolveIdentity(search = '', { hasBaseLock = false } = {}) {
  if (hasBaseLock || readProlificParams(search).prolificId) {
    return { search, storageKey: PARTICIPANT_STORAGE_KEY };
  }
  const arm = readArmOverrideParam(search);
  if (!arm) return { search, storageKey: PARTICIPANT_STORAGE_KEY };
  return {
    search: buildArmOverrideSearch(search, arm),
    storageKey: `${PARTICIPANT_STORAGE_KEY}:${arm}`,
  };
}

export function resolveEntryState({ search = '', session = null, allowResearcher = true } = {}) {
  const params = readProlificParams(search);
  const urlHasProlificId = params.prolificId !== null;
  const urlProlificIdIsValid = isValidProlificId(params.prolificId);
  const researcherRequested = isResearcherRequested(search) && allowResearcher;
  const lockedProlificId = session?.prolificId ?? null;
  const hasStoredAssignment = isExperiment(session?.experiment) && isArm(session?.arm);

  if (lockedProlificId) {
    // A researcher URL (?researcher=1, no incoming PROLIFIC_PID) overrides a
    // stale lock. In prod this branch is dead because allowResearcher is false,
    // so researcherRequested can never be true; the lock only ever suppressed
    // researcher entry in dev/staging, where it was pure friction (a researcher
    // could bypass it with an incognito window anyway).
    if (researcherRequested && !urlHasProlificId) {
      return {
        kind: 'researcher',
        params,
        researcherRequested,
      };
    }
    if (urlHasProlificId && params.prolificId !== lockedProlificId) {
      return {
        kind: 'conflict',
        session,
        params,
        researcherRequested,
      };
    }
    if (!hasStoredAssignment) {
      // Locked to a Prolific ID but assignment never completed (e.g. a prior
      // enroll call failed after the lock was written). Re-enroll with the
      // stored/URL study id; the server derives the experiment from STUDY_ID,
      // so the client no longer needs a valid experiment up front.
      return {
        kind: 'enroll',
        params: {
          prolificId: lockedProlificId,
          studyId: params.studyId ?? session?.studyId ?? null,
          sessionId: params.sessionId ?? session?.sessionId ?? null,
          arm: params.arm ?? null,
        },
        researcherRequested,
        staleSession: session,
      };
    }
    return {
      kind: 'resume',
      session,
      params,
      researcherRequested,
    };
  }

  if (urlHasProlificId) {
    if (!urlProlificIdIsValid) {
      return {
        kind: 'blocked',
        reason: 'invalid',
        params,
        researcherRequested,
      };
    }
    // Experiment/arm are assigned server-side from the Prolific STUDY_ID at
    // enrollment (participants cannot select their experiment). The client only
    // gates on a valid Prolific ID here; an absent or unrecognized STUDY_ID is
    // rejected by the server, surfaced as an enrollment error.
    return {
      kind: 'enroll',
      params: {
        prolificId: params.prolificId,
        studyId: params.studyId,
        sessionId: params.sessionId,
        arm: params.arm ?? null,
      },
      researcherRequested,
    };
  }

  if (researcherRequested) {
    return {
      kind: 'researcher',
      params,
      researcherRequested,
    };
  }

  return {
    kind: 'blocked',
    reason: 'missing',
    params,
    researcherRequested,
  };
}
